/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 14:55:08
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import moment from 'moment';

// Wait ms time
export const waitFor = ms =>
  new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, ms);
  });

/**
 * Check a value is empty or not
 *
 * @param  {[type]} value  specific value
 *
 * @return {boolean}       true or false
 */
const isEmpty = (value: any) =>
  value === undefined || value === null || value.toString() === '' || value <= 0;

/**
 * Get date from time value
 *
 * @param  {number} timeValue
 *
 * @return {string}
 */
export const getDateTime = (timeValue: number) => {
  if (isEmpty(timeValue)) {
    return '';
  }
  const parseTimestamp = parseInt(timeValue) * 1000;
  return moment(parseTimestamp).format('MMM DD Y LT');
};

/**
 * Get hour and minute from value
 *
 * @param  {number} timeValue
 *
 * @return {string}
 */
export const getTimeConvert = (timeValue: number, i18n: any) => {
  if (isEmpty(timeValue)) {
    return '';
  }

  const hour = Math.floor(timeValue / 60);
  const minutes = timeValue % 60;

  let res = '';
  if (hour > 0) {
    res += i18n.translate('AdminHTML.Common.Duration.hour', {
      count: hour,
      nt_number: i18n.formatNumber(hour)
    });
    res += ' ';
  }

  if (minutes > 0) {
    res += i18n.translate('AdminHTML.Common.Duration.minute', {
      count: minutes,
      nt_number: i18n.formatNumber(minutes)
    });
  }

  return res.trim();
};

/**
 * format number to minutes or hours
 * @param  {number} value
 * @return {string}       string under formatting
 */
export const formatSecondsToMinutesOrHours = (value: number, i18n: any) => {
  const seconds = _.isNumber(value) ? value : 0;

  let minutes = Math.floor(seconds / 60);
  if (minutes < 1) minutes = 1;
  if (minutes < 60)
    return i18n.translate('AdminHTML.Common.Duration.minute', {
      count: minutes,
      nt_number: i18n.formatNumber(minutes)
    });

  const hours = Math.floor(value / 3600);
  return i18n.translate('AdminHTML.Common.Duration.hour', {
    count: hours,
    nt_number: i18n.formatNumber(hours)
  });
};

/**
 * format time label by minutes
 * @param {number} minutes
 * @returns format = HH:MM AM/PM - ex: 12:00 AM
 */
const formatTimeLabel = minutes => {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const ampm = hour < 12 ? 'AM' : 'PM';
  const formattedHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  return `${formattedHour}:${minute === 0 ? '00' : minute} ${ampm}`;
};

/**
 * generation options by duration
 * @param {number} duration value = 30min | 15min | ...
 * @returns timeOptions
 */
export const generateTimeOptions = duration => {
  const timeOptions = [];
  const minutesInADay = 24 * 60;

  for (let minutes = 0; minutes < minutesInADay; minutes += duration) {
    const formattedTime = formatTimeLabel(minutes);
    timeOptions.push({ value: minutes, label: formattedTime });
  }

  return timeOptions;
};
